import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import findKey from 'lodash/findKey';
import { compose } from 'redux';
import { useSelector } from 'react-redux';
import HasSomeRights from 'nallian-shared-ui/lib/layout/rights/HasSomeRights';
import { GetRights, GetMyPrincipal } from 'nallian-shared-ui/lib/selectors';
import { hasSomeRights, hasRight } from 'nallian-shared-ui/lib/lib/routesHelper';
import { PageTitle, PageContent, Tab, Link, Menu, Flex } from 'nallian-shared-ui/lib/components';
import { history } from 'nallian-shared-ui/lib/config/history';
import organisationService from '../services/organisationService';
import AppSubscriptions from './appSubscriptions/AppSubscriptions';
import IdentificationCodesTable from './identificationcodes/IdentificationCodesTable';
import { TABS } from '../constants';
import PrincipalsOverview from './principals/PrincipalsOverview';
import PendingInvitations from './pendingInvitations/PendingInvitations';
import UserGroupsOverview from './userGroups/UserGroupsOverview';
import JoinRequestsOverview from './joinRequests/JoinRequestsOverview';
import { isSystemOrganisation } from '../../../app/config/systemOrgs';
import { useCanAccessIdentificationCodes } from '../hooks/useCanAccessIdentificationCodes';
import organisationAdminsService from '../services/organisation/organisationAdminsService';
import OrganisationContactsService from '../services/contacts/organisationContactsService';
import Details from './details/Details';

const TAB_INDEX_MAPPING = {
  [TABS.DETAILS]: 0,
  [TABS.PRINCIPALS]: 1,
  [TABS.PENDING_INVITATIONS]: 2,
  [TABS.JOIN_REQUESTS]: 3,
  [TABS.APP_SUBSCRIPTIONS]: 4,
  [TABS.USER_GROUPS]: 5,
  [TABS.IDENTIFICATIONCODES]: 6,
};

const PRINCIPAL_RIGHTS = [
  'PRINCIPALS.MANAGE.ALL',
  'PRINCIPALS.MANAGE.ALL.EXCEPTSYSTEMORGS',
  'PRINCIPALS.READ.ALL',
  'PRINCIPALS.READ.ORGANISATION',
  'PRINCIPALS.READ.ALL.EXCEPTSYSTEMORGS',
  'PRINCIPALS.MANAGE.ORGANISATION',
];

const PENDING_INVITATION_RIGHTS = [
  'PENDINGINVITATIONS.MANAGE.ALL',
  'PENDINGINVITATIONS.MANAGE.ALL.EXCEPTSYSTEMORGS',
  'PENDINGINVITATIONS.READ.ALL',
  'PENDINGINVITATIONS.READ.ORGANISATION',
  'PENDINGINVITATIONS.MANAGE.ORGANISATION',
  'PENDINGINVITATIONS.READ.ALL.EXCEPTSYSTEMORGS',
];

const ORGANISATION_APPS_RIGHTS = [
  'ORGANISATIONAPPS.MANAGE.ALL',
  'ORGANISATIONAPPS.MANAGE.ALL.EXCEPTSYSTEMORGS',
  'ORGANISATIONAPPS.MANAGE.ORGANISATION',
  'ORGANISATIONAPPS.READ.ALL',
  'ORGANISATIONAPPS.READ.ORGANISATION',
  'ORGANISATIONAPPS.READ.ALL.EXCEPTSYSTEMORGS',
];

const COMMUNITY_APPS_RIGHTS = [
  'COMMUNITYAPPS.MANAGE.ALL',
  'COMMUNITYAPPS.READ.ALL',
  'COMMUNITYAPPS.READ.COMMUNITY',
];

export const ORGANISATION_RIGHTS = [
  'ORGANISATIONS.MANAGE.ALL',
  'ORGANISATIONS.MANAGE.ORGANISATION',
  'ORGANISATIONS.MANAGE.ALL.EXCEPTSYSTEMORGS',
];

const USER_GROUP_RIGHTS = [
  'user-groups/read/non-system-organisation',
  'user-groups/read/system-organisation',
  'user-groups/read/own-organisation',
];

const OrganisationDetails = ({
  organisationFetch,
  dispatchOrganisationGet,
  dispatchOrganisationAdminsGet,
  organisationAdminsFetch,
  dispatchOrganisationContactsGet,
  organisationContactsFetch,
}) => {
  const { t } = useTranslation();
  const { tab, id } = useParams();

  const rights = useSelector(GetRights);
  const myPrincipal = useSelector(GetMyPrincipal);
  const organisationId = id || myPrincipal?.organisation?.id;

  const forbidManageBecauseSystemOrg = useMemo(
    () =>
      isSystemOrganisation(organisationId) &&
      !hasRight(rights, 'ORGANISATIONS.MANAGE.ORGANISATION') &&
      hasSomeRights(rights, [
        'ORGANISATIONS.MANAGE.ALL',
        'ORGANISATIONS.MANAGE.ALL.EXCEPTSYSTEMORGS',
      ]),
    [rights, organisationId]
  );

  useEffect(() => {
    if (organisationId) dispatchOrganisationGet(organisationId);
  }, [organisationId, dispatchOrganisationGet]);

  useEffect(() => {
    if (organisationId) dispatchOrganisationAdminsGet(organisationId);
  }, [dispatchOrganisationAdminsGet, organisationId]);

  useEffect(() => {
    if (organisationId) dispatchOrganisationContactsGet(organisationId);
  }, [dispatchOrganisationContactsGet, organisationId]);

  const urlRoot = useMemo(() => (id ? `/organisations/${id}` : '/organisation'), [id]);

  const organisationName = useMemo(
    () => (organisationFetch.fulfilled ? organisationFetch.value.organisationName : '...'),
    [organisationFetch]
  );

  const { read: canReadIdCodes } = useCanAccessIdentificationCodes(organisationId);

  return (
    <PageContent>
      <PageTitle flexed>
        {id ? (
          <Flex alignItems="center">
            <Link to="/organisations" href="/organisations" marginright="1px">
              {t('organisations')}
            </Link>
            &nbsp;/&nbsp;
            {organisationName}
          </Flex>
        ) : (
          <Flex alignItems="center">
            {t('myOrganisation')}&nbsp;-&nbsp;{organisationName}
          </Flex>
        )}
      </PageTitle>
      <Tab
        activeIndex={TAB_INDEX_MAPPING[tab]}
        onTabChange={(e, { activeIndex }) => {
          const activeTab = findKey(TAB_INDEX_MAPPING, index => index === activeIndex);
          if (activeTab) {
            history.push(`${urlRoot}/${activeTab}`);
          }
        }}
        panes={[
          {
            menuItem: t('details'),
            render: () => (
              <Tab.Pane>
                <Details
                  organisationFetch={organisationFetch}
                  organisationAdminsFetch={organisationAdminsFetch}
                  organisationContactsFetch={organisationContactsFetch}
                  urlRoot={`${urlRoot}/${TABS.DETAILS}`}
                  organisationId={organisationId}
                  forbidManageBecauseSystemOrg={forbidManageBecauseSystemOrg}
                />
              </Tab.Pane>
            ),
          },
          hasSomeRights(rights, PRINCIPAL_RIGHTS)
            ? {
                menuItem: t('users'),
                render: () => (
                  <Tab.Pane>
                    {organisationFetch.fulfilled && (
                      <PrincipalsOverview
                        isOrgAdmin={!id}
                        forbidManageBecauseSystemOrg={forbidManageBecauseSystemOrg}
                        allowDeleted={Boolean(id)}
                        urlRoot={urlRoot}
                        organisationId={organisationId}
                      />
                    )}
                  </Tab.Pane>
                ),
              }
            : undefined,
          hasSomeRights(rights, PENDING_INVITATION_RIGHTS)
            ? {
                menuItem: t('pendingInvitations_plural'),
                render: () => (
                  <Tab.Pane>
                    {organisationFetch.fulfilled && (
                      <PendingInvitations
                        forbidManageBecauseSystemOrg={forbidManageBecauseSystemOrg}
                        urlRoot={urlRoot}
                        organisationId={organisationId}
                      />
                    )}
                  </Tab.Pane>
                ),
              }
            : undefined,
          hasSomeRights(rights, PENDING_INVITATION_RIGHTS)
            ? {
                menuItem: 'Join Requests',
                render: () => (
                  <Tab.Pane>
                    {organisationFetch.fulfilled && (
                      <JoinRequestsOverview
                        forbidManageBecauseSystemOrg={forbidManageBecauseSystemOrg}
                        urlRoot={urlRoot}
                        organisationId={organisationId}
                      />
                    )}
                  </Tab.Pane>
                ),
              }
            : undefined,
          hasSomeRights(rights, ORGANISATION_APPS_RIGHTS) &&
          hasSomeRights(rights, COMMUNITY_APPS_RIGHTS)
            ? {
                menuItem: t('appSubscriptions'),
                render: () => (
                  <Tab.Pane>
                    {organisationFetch.fulfilled && (
                      <AppSubscriptions
                        forbidManageBecauseSystemOrg={forbidManageBecauseSystemOrg}
                        urlRoot={urlRoot}
                        organisationId={organisationId}
                      />
                    )}
                  </Tab.Pane>
                ),
              }
            : undefined,
          hasSomeRights(rights, USER_GROUP_RIGHTS) // TODO: to adjust rights
            ? {
                menuItem: 'User Groups',
                render: () => (
                  <Tab.Pane>
                    {organisationFetch.fulfilled && (
                      <UserGroupsOverview
                        forbidManageBecauseSystemOrg={forbidManageBecauseSystemOrg}
                        urlRoot={urlRoot}
                        organisationId={organisationId}
                      />
                    )}
                  </Tab.Pane>
                ),
              }
            : undefined,
          canReadIdCodes
            ? {
                menuItem: t('identificationCodes'),
                render: () => (
                  <Tab.Pane>
                    {organisationFetch.fulfilled && (
                      <IdentificationCodesTable urlRoot={urlRoot} organisationId={organisationId} />
                    )}
                  </Tab.Pane>
                ),
              }
            : undefined,
          {
            menuItem: (
              <Menu.Menu position="right" key="menu">
                <HasSomeRights rights={ORGANISATION_RIGHTS}>
                  {tab === TABS.DETAILS && id && !forbidManageBecauseSystemOrg ? (
                    <Menu.Item
                      icon="pencil"
                      content="Edit"
                      onClick={() => history.push(`${urlRoot}/edit`)}
                    />
                  ) : (
                    <div />
                  )}
                </HasSomeRights>
              </Menu.Menu>
            ),
          },
        ]}
      />
    </PageContent>
  );
};

OrganisationDetails.propTypes = {
  dispatchOrganisationGet: PropTypes.func.isRequired,
  organisationFetch: PropTypes.object.isRequired,
  dispatchOrganisationAdminsGet: PropTypes.func.isRequired,
  organisationAdminsFetch: PropTypes.object.isRequired,
  dispatchOrganisationContactsGet: PropTypes.func.isRequired,
  organisationContactsFetch: PropTypes.object.isRequired,
};

export default compose(
  organisationService,
  organisationAdminsService,
  OrganisationContactsService
)(OrganisationDetails);
